import {
  Component, Input, OnChanges, AfterViewInit,
  ViewChild, ElementRef, NgZone, HostListener,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { RecognitionVisualizationConfig, RecognitionRow, RecognitionObject } from '../bbox-gallery-card/bbox-gallery-card.component';

// ─────────────────────────────────────────────────────────────────────────────

const TAG_META: Record<string, { label: string; color: string; fill: string }> = {
  postobon:    { label: 'Postobon',    color: '#6366f1', fill: 'rgba(99,102,241,.35)'  },
  competencia: { label: 'Competencia', color: '#f43f5e', fill: 'rgba(244,63,94,.35)'   },
  precios:     { label: 'Precios',     color: '#f59e0b', fill: 'rgba(245,158,11,.35)'  },
  nevera:      { label: 'Nevera',      color: '#14b8a6', fill: 'rgba(20,184,166,.22)'  },
};

interface TooltipState {
  visible: boolean;
  x: number;
  y: number;
  name: string;
  tag: string;
  score: number;
  tagColor: string;
}

@Component({
  selector: 'app-planogram-card',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './planogram-card.component.html',
  styleUrl:    './planogram-card.component.scss',
})
export class PlanogramCardComponent implements OnChanges, AfterViewInit {
  @Input() config!: RecognitionVisualizationConfig;
  @Input() loading = false;

  @ViewChild('planCanvas') canvasRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('canvasWrap') wrapRef!: ElementRef<HTMLDivElement>;

  // ── Estado ────────────────────────────────────────────────────────────────
  currentIdx  = 0;
  activeTags  = new Set<string>(['postobon', 'competencia', 'precios', 'nevera']);
  tags: { key: string; label: string; color: string }[] = [];

  tooltip: TooltipState = { visible: false, x: 0, y: 0, name: '', tag: '', score: 0, tagColor: '' };

  // ── Dimensiones de referencia ─────────────────────────────────────────────
  private refW = 1;
  private refH = 1;
  private viewReady = false;

  // ── Mapa de bboxes renderizados para hit-testing ──────────────────────────
  private hitBoxes: { obj: RecognitionObject; cx: number; cy: number; cw: number; ch: number }[] = [];

  constructor(private zone: NgZone) {}

  ngOnChanges(): void {
    this.build();
    if (this.viewReady) this.render();
  }

  ngAfterViewInit(): void {
    this.viewReady = true;
    this.render();
  }

  // ── Build ─────────────────────────────────────────────────────────────────

  private build(): void {
    if (!this.config?.rows?.length) return;

    const allObjs = this.config.rows.flatMap(r => r.objects);
    const tagKeys = [...new Set(allObjs.map(o => o.tag))].filter(t => TAG_META[t]);
    this.tags = tagKeys.map(t => ({ key: t, label: TAG_META[t].label, color: TAG_META[t].color }));

    let maxX = 1, maxY = 1;
    for (const obj of allObjs) {
      if (obj.bbox.x_max > maxX) maxX = obj.bbox.x_max;
      if (obj.bbox.y_max > maxY) maxY = obj.bbox.y_max;
    }
    this.refW = maxX;
    this.refH = maxY;

    if (this.currentIdx >= this.config.rows.length) this.currentIdx = 0;
  }

  // ── Navegación ────────────────────────────────────────────────────────────

  prev(): void { if (this.currentIdx > 0) { this.currentIdx--; this.render(); } }
  next(): void { if (this.currentIdx < this.config.rows.length - 1) { this.currentIdx++; this.render(); } }

  goTo(i: number): void { this.currentIdx = i; this.render(); }

  toggleTag(key: string): void {
    if (this.activeTags.has(key)) this.activeTags.delete(key);
    else this.activeTags.add(key);
    this.render();
  }

  isTagActive(key: string): boolean { return this.activeTags.has(key); }

  // ── Render canvas ─────────────────────────────────────────────────────────

  render(): void {
    if (!this.canvasRef || !this.config?.rows?.length) return;
    this.zone.runOutsideAngular(() => this.draw());
  }

  private draw(): void {
    const canvas = this.canvasRef.nativeElement;
    const ctx    = canvas.getContext('2d');
    if (!ctx) return;

    const W = canvas.width;
    const H = canvas.height;
    const scaleX = W / this.refW;
    const scaleY = H / this.refH;

    ctx.clearRect(0, 0, W, H);

    // Fondo
    ctx.fillStyle = '#0a0a14';
    ctx.fillRect(0, 0, W, H);

    // Grid sutil
    ctx.strokeStyle = 'rgba(255,255,255,.04)';
    ctx.lineWidth = 1;
    const step = 50;
    for (let x = 0; x < W; x += step) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke(); }
    for (let y = 0; y < H; y += step) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke(); }

    const row = this.config.rows[this.currentIdx];
    if (!row) return;

    this.hitBoxes = [];

    // Orden de render: nevera primero (fondo), luego el resto
    const ORDER = ['nevera', 'precios', 'postobon', 'competencia'];
    const sorted = [...row.objects].sort((a, b) =>
      (ORDER.indexOf(b.tag) - ORDER.indexOf(a.tag))
    );

    for (const obj of sorted) {
      if (!this.activeTags.has(obj.tag)) continue;
      const meta = TAG_META[obj.tag];
      if (!meta) continue;

      const cx = Math.round(obj.bbox.x_min * scaleX);
      const cy = Math.round(obj.bbox.y_min * scaleY);
      const cw = Math.max(2, Math.round(obj.bbox.width  * scaleX));
      const ch = Math.max(2, Math.round(obj.bbox.height * scaleY));

      // Fill con opacidad proporcional al score
      const alpha = 0.15 + obj.score * 0.4;
      ctx.fillStyle = this.hexToRgba(meta.color, alpha);
      ctx.fillRect(cx, cy, cw, ch);

      // Borde
      ctx.strokeStyle = meta.color;
      ctx.lineWidth   = obj.tag === 'nevera' ? 1 : 1.5;
      ctx.globalAlpha = Math.max(0.4, obj.score);
      ctx.strokeRect(cx + 0.5, cy + 0.5, cw - 1, ch - 1);
      ctx.globalAlpha = 1;

      // Label si el rect es suficientemente grande
      if (cw > 30 && ch > 14 && obj.name !== 'Others') {
        ctx.fillStyle = '#fff';
        ctx.font      = `bold ${Math.min(10, cw / 5)}px system-ui, sans-serif`;
        ctx.fillStyle = meta.color;
        ctx.globalAlpha = 0.9;
        const label = obj.name.length > 18 ? obj.name.slice(0, 16) + '…' : obj.name;
        ctx.fillText(label, cx + 3, cy + 11);
        ctx.globalAlpha = 1;
      }

      this.hitBoxes.push({ obj, cx, cy, cw, ch });
    }

    // Etiqueta del reconocimiento
    ctx.fillStyle = 'rgba(0,0,0,.5)';
    ctx.fillRect(0, H - 22, W, 22);
    ctx.fillStyle = 'rgba(255,255,255,.5)';
    ctx.font = '10px system-ui, sans-serif';
    ctx.fillText(`Reconocimiento ${this.currentIdx + 1} / ${this.config.rows.length}`, 8, H - 8);
  }

  // ── Mouse hover ───────────────────────────────────────────────────────────

  @HostListener('mousemove', ['$event'])
  onMouseMove(e: MouseEvent): void {
    if (!this.canvasRef) return;
    const canvas = this.canvasRef.nativeElement;
    const rect   = canvas.getBoundingClientRect();
    const scaleX = canvas.width  / rect.width;
    const scaleY = canvas.height / rect.height;
    const mx = (e.clientX - rect.left) * scaleX;
    const my = (e.clientY - rect.top)  * scaleY;

    // Buscar el bbox más pequeño que contiene el cursor (más específico primero)
    const hit = this.hitBoxes
      .filter(b => mx >= b.cx && mx <= b.cx + b.cw && my >= b.cy && my <= b.cy + b.ch)
      .sort((a, b) => (a.cw * a.ch) - (b.cw * b.ch))[0];

    this.zone.run(() => {
      if (hit) {
        const meta = TAG_META[hit.obj.tag] ?? { color: '#94a3b8' };
        this.tooltip = {
          visible:  true,
          x: e.offsetX + 14,
          y: e.offsetY - 10,
          name:     hit.obj.name,
          tag:      hit.obj.tag,
          score:    Math.round(hit.obj.score * 100),
          tagColor: meta.color,
        };
      } else {
        this.tooltip = { ...this.tooltip, visible: false };
      }
    });
  }

  @HostListener('mouseleave')
  onMouseLeave(): void {
    this.zone.run(() => { this.tooltip = { ...this.tooltip, visible: false }; });
  }

  // ── Stats del reconocimiento actual ──────────────────────────────────────

  get currentRow(): RecognitionRow | null {
    return this.config?.rows?.[this.currentIdx] ?? null;
  }

  tagCount(tag: string): number {
    return this.currentRow?.objects.filter(o => o.tag === tag && o.name !== 'Others').length ?? 0;
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  private hexToRgba(hex: string, alpha: number): string {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r},${g},${b},${alpha})`;
  }

  trackByIdx(i: number): number { return i; }
  scorePct(s: number): string   { return `${s}%`; }

  get totalRows(): number { return this.config?.rows?.length ?? 0; }
}
